import type * as T from "shared/datamodel/schemas/voting-session";
import { SyncService } from "frontend/services/syncService";
import { useSubscribe } from "replicache-react";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import { ReadTransaction } from "@workcanvas/reflect";
import { sum, values, filter, compose } from "rambda";
import {
  getActiveVotingSession,
  getAllVotingSessions,
  prefixForUserDataInSession,
  sessionId,
  thumbnailsKey,
  userDataKey,
} from "shared/datamodel/voting";

import { atomWithReducer } from "jotai/utils";
import { clamp } from "utils/math-utils";

/******************************

Participation status in a voting session

Stored in atom, which holds the pair [session-id, participation-status]
To join a session: set (id, Joined)
To decline: set (id, Declined)
etc..
To check status: atom[0] == cur-running-id && atom[1] == Joined
if atom[0] != cur-running-id, the information is stale and atom[1] can be ignored

******************************/

export const enum Participation {
  NA, // Not applicable (no voting session in progress)
  NotDecided,
  Joined,
  Done,
  Declined,
}

export interface MyVotingStatus {
  sessionId: number;
  participation: Participation;
  votes: Record<string, number>;
}
export type VoteAtomType = MyVotingStatus | undefined;

type VoteActions =
  | { type: "join"; sessionId: number }
  | { type: "done"; sessionId: number }
  | { type: "vote"; elementId: string; vote: number; maxVotesPerUser: number; maxVotesOnObject: number };

function voteAtomReducer(state: VoteAtomType, action: VoteActions): VoteAtomType {
  switch (action.type) {
    case "join": {
      return { sessionId: action.sessionId, participation: Participation.Joined, votes: {} };
    }
    case "done": {
      return { sessionId: action.sessionId, participation: Participation.Done, votes: state?.votes || {} };
    }
    case "vote": {
      const { votes = {} } = state ?? {};

      // no change if exceeding max allowed votes total
      if (sumValuesOfObject(votes) + action.vote > action.maxVotesPerUser) {
        return state;
      }
      const votesOnElement = votes[action.elementId] ?? 0;
      const newVotes = clamp(votesOnElement + action.vote, 0, action.maxVotesOnObject);
      if (state) {
        return { ...state, votes: { ...votes, [action.elementId]: newVotes } };
      } else {
        return state;
      }
    }
  }
  return state;
}

export const votingAtom = atomWithReducer(undefined as VoteAtomType, voteAtomReducer);

export function checkParticipation(voteStatus: VoteAtomType, session?: T.VotingSession | null) {
  if (!session || !voteStatus) {
    return Participation.NA;
  }
  if (voteStatus.sessionId === sessionId(session)) {
    return voteStatus.participation;
  }
  return Participation.NotDecided;
}

// ----------------------

type Maybe<T> = T | null | undefined;
type MaybeSession = Maybe<T.VotingSession>;
type OldOrNew = T.LegacyVotingSession | T.VotingSession;

export function useActiveVotingSession(syncService: SyncService<RW> | null): MaybeSession {
  return useSubscribe(syncService?.getReplicache?.(), getActiveVotingSession, null);
}

export function useAllSessions(syncService: SyncService<RW> | null): OldOrNew[] {
  return useSubscribe(syncService?.getReplicache?.(), getAllVotingSessions, []);
}

function oldVoterIdToNew(user: any): T.VoterId {
  if (user.type == "known") {
    return { type: "known", id: user.id };
  } else {
    return { type: "anonymous", id: user.id, name: user.name, avatar: user.avatar };
  }
}

export function useVotingSessionResults(syncService: SyncService<RW> | null, sessionId?: number): T.VoterData[] {
  return useSubscribe(
    syncService?.getReplicache(),
    async (tx: ReadTransaction) => {
      if (sessionId !== undefined) {
        // try legacy sessions first-
        const legacy = (await tx.get(`voting-${sessionId}`)) as T.LegacyVotingSession;
        if (legacy) {
          // convert the old data format to the new one
          const { votersInfo, votesCast } = legacy;
          const result: T.VoterData[] = [];
          for (const userKey in votersInfo) {
            const id = oldVoterIdToNew(votersInfo[userKey]);
            const votes: Record<string, number> = {};
            for (const elementId in votesCast) {
              for (const userid of votesCast[elementId]) {
                if (userid == userKey) {
                  votes[elementId] ??= 0;
                  votes[elementId]++;
                }
              }
            }
            result.push({ id, votes });
          }
          return result;
        } else {
          const prefix = prefixForUserDataInSession(sessionId);
          return (await tx.scan({ prefix }).values().toArray()) as T.VoterData[];
        }
      }
      return [];
    },
    [],
    [sessionId]
  );
}

export function useVotingSessionThumbnails(
  syncService: SyncService<RW> | null,
  sessionId: number
): Record<string, string> {
  return useSubscribe(
    syncService?.getReplicache(),
    async (tx: ReadTransaction) => {
      const value = await tx.get(thumbnailsKey(sessionId));
      if (!value) return {};
      return value as Record<string, string>;
    },
    {}
  );
}

export function useVotingSession_user(
  syncService: SyncService<RW>,
  sessionId: number,
  userId?: any
): Maybe<T.VoterData> {
  return useSubscribe(
    syncService.getReplicache(),
    async (tx: ReadTransaction) => {
      return (await tx.get(userDataKey(sessionId, userId ?? tx.clientID))) as T.VoterData;
    },
    null
  );
}

/******************************

For a single voting session, the votes are spread out across multiple key-value pairs in reflect.
  key: voting/u/${sessionId}/${userId}
  value: { id: VoterId, votes: Record<string,number> }

The UI needs the data per object. for each element that has votes we need:
- element-id, total votes on element, users who voted for element
and we also need total votes on all.
so some functions to convert the data to the format needed by the UI:
******************************/

const isPositive = (x: number) => x > 0;

export const sumValuesOfObject = compose(sum, values);

const countVotesOfUser = (voter: T.VoterData) => sumValuesOfObject(voter.votes);

const wasVoterActiveInSession = compose(isPositive, countVotesOfUser);

export const filterOnlyActiveVotersInSession = filter(wasVoterActiveInSession);

// Type of summary of vote session results
type Summary = {
  [itemId: string]: {
    total: number;
    ids: T.VoterData["id"][];
  };
};

// Convert the list of vote-info from users, to an object from item-id to
// those who voted for it and total votes (filter out empty votes, which can
// appear in the raw data if a user cancelled their vote)
export function summarizeVotes(votes: T.VoterData[]): Summary {
  return votes.reduce((acc, { id, votes }) => {
    for (const [itemId, voteCount] of Object.entries(votes)) {
      if (voteCount > 0) {
        acc[itemId] ||= { total: 0, ids: [] };
        acc[itemId].total += voteCount;
        acc[itemId].ids.push(id);
      }
    }
    return acc;
  }, {} as Summary);
}

export function secondsLeft(session: MaybeSession): number {
  if (!session || !session.start || !session.duration) return -1;
  const endTime = session.start + session.duration * 1000;
  return Math.ceil((endTime - Date.now()) / 1000);
}

export function formatVotesLeft(votes: number) {
  return votes == 1 ? "1 vote left" : `${votes} votes left`;
}

export function timeLeftForSession(session: Maybe<{ start: number; duration?: number }>) {
  if (!session) return;
  if (!session.duration) return;
  if (session.start == 0) return;
  return session.start + session.duration * 1000 - Date.now();
}
